<script lang="ts">
  import Link from '#app/lib/components/link.svelte'
  import { icon } from '#app/lib/icons'
  import { getTranslatedLanguagesData, type LanguageData } from '#app/lib/languages/languages'
  import { LogError } from '#app/lib/loggers'
  import { translate } from '#app/lib/urls'
  import Dropdown from '#components/dropdown.svelte'
  import { getCurrentLang, I18n, i18n } from '#user/lib/i18n'
  import { mainUser } from '#user/lib/main_user'
  import { setNewLanguage, updateUserAttribute } from '#user/lib/serialized_main_user'
  import Spinner from './spinner.svelte'
  import type { WikimediaLanguageCode } from 'wikibase-sdk'

  const currentLang = getCurrentLang()

  let translatedLanguages: LanguageData[] | undefined = $state()
  let currentLanguageLabel: string | undefined = $state()

  const waitingForTranslatedLanguages = getTranslatedLanguagesData()
    .then(res => {
      translatedLanguages = res
      currentLanguageLabel = translatedLanguages
        .find(langInfo => langInfo.lang === currentLang)?.label
    })
    .catch(LogError)

  function selectLang (lang: WikimediaLanguageCode) {
    if (mainUser) updateUserAttribute('language', lang)
    else setNewLanguage(lang)
  }
</script>

<div class="language-picker">
  {#await waitingForTranslatedLanguages}
    <Spinner light={true} />
  {:then}
    <Dropdown dropdownWidthBaseInEm={18}>
      {#snippet buttonInner()}
        <div class="button-inner">
          <span class="current-lang-label">{currentLanguageLabel}</span>
          <span class="current-lang">{currentLang}</span>
          {@html icon('caret-down')}
        </div>
      {/snippet}
      {#snippet dropdownContent()}
        <div class="dropdown-content">
          <ul class="options">
            {#each translatedLanguages as { lang, label, completion } (lang)}
              <li class="option">
                <button
                  title="{i18n('switch_to_lang', { language: label })} ({i18n('translation_completion', { completion })})"
                  onclick={() => selectLang(lang)}
                >
                  <span class="completion" style:width={`${completion}%`}></span>
                  <span class="lang-label">{label}</span>
                  <span class="lang">({lang})</span>
                  <span class="completion-text">{completion}%</span>
                </button>
              </li>
            {/each}
          </ul>
          <div class="help-translate">
            <Link
              url={translate}
              icon="pencil"
              text={I18n('help translate Inventaire')}
            />
          </div>
        </div>
      {/snippet}
    </Dropdown>
  {/await}
</div>

<style lang="scss">
  @import "general/scss/utils";
  .language-picker{
    margin-inline-start: 0.2em;
    align-self: stretch;
    @include display-flex(row, stretch, center);
    :global(.dropdown-button){
      @include bg-hover-lighten-svelte($topbar-bg-color);
      padding-inline-start: 0.5em;
    }
    :global(.fa-caret-down){
      color: #ccc;
    }
    :global(.spinner){
      width: 4rem;
      @include display-flex(row, center, center);
    }
  }
  .button-inner{
    @include serif;
    font-weight: bold;
    color: white;
    white-space: nowrap;
    span{
      color: white;
    }
  }
  .options{
    overflow-y: auto;
    overflow-x: hidden;
    max-height: 60vh;
  }
  li.option{
    @include display-flex(row, stretch);
    button{
      flex: 1 0 auto;
      padding: 0.4em 0;
      position: relative;
      z-index: 1;
      background-color: $grey;
      border-radius: 0;
      text-align: start;
      @include display-flex(row);
      &, span{
        color: white;
      }
      &:focus{
        background-color: $dark-grey;
        .completion{
          opacity: 0.25;
          background-color: $dark-grey;
        }
      }
    }
    .completion{
      position: absolute;
      inset-block: 0;
      inset-inline-start: 0;
      z-index: -1;
      background-color: $light-blue;
    }
    .lang-label{
      font-weight: bold;
      margin-inline: 0.5rem 0.2rem;
    }
    .lang{
      color: $light-grey;
      font-size: 0.9rem;
      margin-inline-end: 0.5rem;
    }
    .completion-text{
      margin-inline-start: auto;
      margin-inline-end: 0.5rem;
      font-weight: normal;
      opacity: 0.8;
    }
    &:hover{
      .completion{
        opacity: 0.25;
      }
      button{
        background-color: $dark-grey;
      }
    }
    &:not(:last-child) .completion{
      border-end-end-radius: $global-radius;
    }
  }
  .help-translate{
    :global(a){
      @include display-flex(row, center, flex-start);
      flex: 1 0 auto;
      align-self: stretch;
      font-weight: bold;
      @include serif;
      @include bg-hover-lighten-svelte($topbar-bg-color);
      font-size: 1rem;
      padding: 0.8rem 1rem;
      @include radius-bottom;
    }
    :global(.fa), :global(.link-text){
      color: white;
    }
  }
  /* Not Too Small screens */
  @media screen and (width >= $very-small-screen){
    .current-lang{
      display: none;
    }
  }
  /* Very small screens */
  @media screen and (width < $very-small-screen){
    .current-lang-label{
      display: none;
    }
  }
</style>
